import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Subject,
  defaultIfEmpty,
  firstValueFrom,
  takeUntil,
  timer,
} from 'rxjs';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { environment } from 'src/environments/environment';
import { AIChatbotAttributeData, AIChatbotMessage, AIChatbotMessageContent } from '../../models/ai-chatbot-message';
import * as AIChatbotMockData from '../ai-chatbot/ai-chatbot-mock-data';
import { AIChatbotInterpretationResponse } from '../../models/ai-chatbot-interpretation-response';
import { AIChatbotSession } from '../../models/ai-chatbot-session';

@Injectable({ providedIn: 'root' })
export class AIChatbotApiService {
  constructor(
    private http: HttpClient,
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService
  ) {}

  async interpret(
    session: AIChatbotSession,
    message: string,
    content: AIChatbotMessageContent,
    cancellationSubject: Subject<void>
  ): Promise<AIChatbotInterpretationResponse> {
    const { url, headers, body } = this.getChatApiCallParameters(
      session,
      message,
      content
    );

    const apiResponse = await firstValueFrom(
      this.http.post<AIChatbotInterpretationResponse>(url, body, { headers })
        .pipe(
          takeUntil(cancellationSubject), defaultIfEmpty(null)
        )
    );

    if (apiResponse === null) {
      throw 'processing_canceled';
    }

    return apiResponse;

  }

  async generate(
    message: string,
    history: AIChatbotMessage[],
    cancellationSubject: Subject<void>,
    interpretationResponse: AIChatbotInterpretationResponse,
    sessionId: string
  ): Promise<AIChatbotMessage['content']> {
    // const { url, headers, body } = this.getChatApiCallParameters(
    //   message,
    //   history
    // );

    // Replace timer() for the actual API call
    const apiResponse = await firstValueFrom(
      timer(1000).pipe(takeUntil(cancellationSubject), defaultIfEmpty(null))
    );
    // const apiResponse = this.http.post<AIChatbotGenerationResponse>(url, body, { headers });

    if (apiResponse === null) {
      throw 'processing_canceled';
    }

    let response;
    if (['generating_presentations', 'template_search'].includes(interpretationResponse.type)) {
      response = { ...AIChatbotMockData.generatedTemplatesResponse };
    } else if (
      interpretationResponse.type === 'generating_single_presentation'
    ) {
      response = { ...AIChatbotMockData.generatedSinglePresentationResponse };
    } else {
      response = {};
    }

    response.message = interpretationResponse.loadingMessage;
    return response;
  }

  async generatePresentationVariation(
    sessionId: string,
    productCode: string,
    concept: string,
    semanticScore: number,
    cancellationSubject: Subject<void>
  ): Promise<AIChatbotAttributeData> {
    const { url, headers, body } = this.getGenerateVariationApiCallParameters(
      sessionId,
      productCode,
      concept,
      semanticScore
    );

    // Replace timer() for the actual API call
    const apiResponse = await firstValueFrom(
      this.http.post<AIChatbotAttributeData>(url, body, { headers })
        .pipe(
          takeUntil(cancellationSubject), defaultIfEmpty(null)
        )
      );

    if (apiResponse === null) {
      throw 'processing_canceled';
    }

    return apiResponse;
  }

  async generatePresentationColors(
    sessionId: string,
    variationId: number,
    attributeData: AIChatbotAttributeData,
    cancellationSubject: Subject<void>
  ): Promise<AIChatbotAttributeData> {
    const { url, headers, body } = this.getGenerateColorsApiCallParameters(
      sessionId,
      variationId,
      attributeData
    );

    // Replace timer() for the actual API call
    const apiResponse = await firstValueFrom(
      this.http.post<AIChatbotAttributeData>(url, body, { headers })
        .pipe(
          takeUntil(cancellationSubject), defaultIfEmpty(null)
        )
      );

    if (apiResponse === null) {
      throw 'processing_canceled';
    }

    return apiResponse;
  }

  getApiCallParameters(endpoint: string, sessionId: string): any {
    const baseUrl = environment.AI_SERVICE_URL;
    const apiKey = environment.AI_SERVICE_API_KEY;
    const url = `${baseUrl}/api/v2/${endpoint}`;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    });

    const body = {
      token: this.userStateService.getAccessToken().access_token,
      companyId: this.companyStateService.getSelectedCompanyId(),
      sessionId
    };

    return { url, headers, body };
  }

  getChatApiCallParameters(
    session: AIChatbotSession,
    message: string,
    content: AIChatbotMessageContent
  ) {
    const callParams = this.getApiCallParameters('chat', session.id);

    const history = session.messages$.getValue();
    const cleanHistory = history.filter((msg) => msg.role !== 'error');
    const payload = {
      style: content.categories?.style,
      industry: content.categories?.industry,
      components: content.categories?.contents,
      attributeData: session.presentation?.attributeData,
      templates: session.latestTemplates,
      excludedProductCodes: session.excludedProductCodes,
    };

    callParams.body.message = message;
    callParams.body.history = cleanHistory.slice(0, -1);
    callParams.body.payload = payload;

    return callParams;
  }

  getGenerateVariationApiCallParameters(
    sessionId: string,
    productCode: string,
    concept: string,
    semanticScore: number
  ) {
    const callParams = this.getApiCallParameters(`presentations/generate-variation/${productCode}`, sessionId);

    callParams.body.concept = concept;
    callParams.body.semanticScore = semanticScore;

    return callParams;
  }

  getGenerateColorsApiCallParameters(
    sessionId: string,
    variationId: number,
    attributeData: AIChatbotAttributeData,
  ) {
    const callParams = this.getApiCallParameters(`presentations/generate-colors/${variationId}`, sessionId);

    callParams.body.attributeData = attributeData;

    return callParams;
  }
}
